'use client';

import { useState, useCallback, useTransition, useMemo, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import { Search, Calendar, Clock, User, ChevronLeft, ChevronRight, Tag, Filter, X, FileText } from 'lucide-react';
import { BlogListResponse, BlogCategory, BlogPost } from '@/types/blog';
import { formatReadingTime } from '@/lib/reading-time';
import { useDebounce } from '@/hooks/useDebounce';
import { HighlightText } from '@/components/HighlightText';

interface BlogListClientProps {
  initialData: BlogListResponse;
  categories: BlogCategory[];
  initialFilters: {
    category?: string;
    tag?: string;
    search?: string;
    featured?: boolean;
  };
  initialPagination: {
    page: number;
    limit: number;
  };
}

export default function BlogListClient({
  initialData,
  categories,
  initialFilters,
  initialPagination
}: BlogListClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  
  // State management - initial data is already sorted by the API
  const [blogData, setBlogData] = useState(initialData);
  const [filters, setFilters] = useState(initialFilters);
  const [searchTerm, setSearchTerm] = useState(initialFilters.search || '');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  
  // Debounce search term for better performance (reduced to 200ms for snappier feel)
  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  // Create search parameters URL
  const createSearchParams = useCallback((newFilters: typeof filters, page: number = 1) => {
    const params = new URLSearchParams();
    
    if (newFilters.category) params.set('category', newFilters.category);
    if (newFilters.tag) params.set('tag', newFilters.tag);
    if (newFilters.search) params.set('search', newFilters.search);
    if (newFilters.featured) params.set('featured', 'true');
    if (page > 1) params.set('page', page.toString());
    
    return params.toString();
  }, []);

  // Fetch filtered data from API
  const fetchFilteredData = useCallback(async (newFilters: typeof filters, page: number = 1) => {
    try {
      const params = new URLSearchParams();
      
      if (newFilters.tag) params.set('tag', newFilters.tag);
      if (newFilters.search) params.set('search', newFilters.search);
      if (newFilters.featured) params.set('isFeatured', 'true');
      params.set('page', page.toString());
      params.set('pageSize', '8'); // Reduced for better performance
      params.set('sortBy', 'publishedAt');
      params.set('sortDirection', 'desc');
      
      const apiUrl = `/api/blog${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }
      
      const result = await response.json();
      
      // Transform API response to match expected format
      const transformedPosts = result.data.map((post: any) => ({
        id: post.id.toString(),
        slug: post.slug,
        title: post.title,
        excerpt: post.excerpt || '',
        content: post.excerpt || '', // Use excerpt instead of body for performance
        featuredImage: post.featuredImage,
        author: {
          name: post.authorName || (post.author ? `${post.author.firstName} ${post.author.lastName}`.trim() : ''),
          avatar: undefined,
          role: undefined
        },
        publishedAt: post.publishedAt,
        updatedAt: post.updatedAt,
        readingTime: {
          minutes: post.readingTimeMinutes || 5,
          text: `${post.readingTimeMinutes || 5} min read`
        },
        tags: Array.isArray(post.tags) ? post.tags : [],
        categories: [], // Will be filtered client-side for categories since they're mock data
        featured: post.isFeatured || false,
        published: post.status === 'published',
        seo: {
          metaTitle: post.title,
          metaDescription: post.metaDescription || post.excerpt || undefined,
          keywords: Array.isArray(post.seoKeywords) ? post.seoKeywords : []
        }
      }));

      // Posts are already sorted by the API (publishedAt desc)
      let filteredPosts = transformedPosts;
      
      // Apply client-side category filter (categories are based on tags)
      if (newFilters.category) {
        // Category filtering is done by filtering posts that have the category as a tag
        filteredPosts = transformedPosts.filter((post: BlogPost) =>
          post.tags.some((tag: string) => tag.toLowerCase() === newFilters.category!.toLowerCase())
        );
        
        // If no posts found from API with this category, try to get all posts and filter
        if (filteredPosts.length === 0) {
          const allPostsParams = new URLSearchParams();
          allPostsParams.set('pageSize', '100'); // Get all posts
          allPostsParams.set('sortBy', 'publishedAt');
          allPostsParams.set('sortDirection', 'desc');
          
          const allPostsResponse = await fetch(`/api/blog?${allPostsParams.toString()}`);
          if (allPostsResponse.ok) {
            const allPostsResult = await allPostsResponse.json();
            const allTransformedPosts = allPostsResult.data.map((post: any) => ({
              id: post.id.toString(),
              slug: post.slug,
              title: post.title,
              excerpt: post.excerpt || '',
              content: post.excerpt || '', // Use excerpt instead of body for performance
              featuredImage: post.featuredImage,
              author: {
                name: post.authorName || (post.author ? `${post.author.firstName} ${post.author.lastName}`.trim() : ''),
                avatar: undefined,
                role: undefined
              },
              publishedAt: post.publishedAt,
              updatedAt: post.updatedAt,
              readingTime: {
                minutes: post.readingTimeMinutes || 5,
                text: `${post.readingTimeMinutes || 5} min read`
              },
              tags: Array.isArray(post.tags) ? post.tags : [],
              categories: Array.isArray(post.tags) ? post.tags : [], // Use tags as categories
              featured: post.isFeatured || false,
              published: post.status === 'published',
              seo: {
                metaTitle: post.title,
                metaDescription: post.metaDescription || post.excerpt || undefined,
                keywords: Array.isArray(post.seoKeywords) ? post.seoKeywords : []
              }
            }));
            
            // Posts are already sorted by the API (publishedAt desc)
            filteredPosts = allTransformedPosts.filter((post: BlogPost) =>
              post.tags.some((tag: string) => tag.toLowerCase() === newFilters.category!.toLowerCase())
            );
          }
        }
        
        // Apply additional filters on the category-filtered posts
        if (newFilters.search) {
          const searchTerm = newFilters.search.toLowerCase();
          filteredPosts = filteredPosts.filter((post: BlogPost) =>
            post.title.toLowerCase().includes(searchTerm) ||
            post.excerpt.toLowerCase().includes(searchTerm)
            // Removed content search for performance since content = excerpt now
          );
        }
        
        if (newFilters.featured) {
          filteredPosts = filteredPosts.filter((post: BlogPost) => post.featured);
        }
        
        if (newFilters.tag && newFilters.tag !== newFilters.category) {
          filteredPosts = filteredPosts.filter((post: BlogPost) =>
            post.tags.some((tag: string) => tag.toLowerCase() === newFilters.tag!.toLowerCase())
          );
        }
      }

      // Calculate pagination for filtered results
      const totalPosts = newFilters.category ? filteredPosts.length : (result.pagination?.totalItems || filteredPosts.length);
      const postsPerPage = 8; // Reduced for better performance
      const totalPages = Math.ceil(totalPosts / postsPerPage);
      const startIndex = (page - 1) * postsPerPage;
      const endIndex = startIndex + postsPerPage;
      const paginatedPosts = newFilters.category ? filteredPosts.slice(startIndex, endIndex) : filteredPosts;

      return {
        posts: paginatedPosts,
        totalPosts,
        currentPage: page,
        totalPages,
        postsPerPage,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1
      };
    } catch (error) {
      console.error('Error fetching filtered data:', error);
      // Return empty result if API fails
      return {
        posts: [],
        totalPosts: 0,
        currentPage: page,
        totalPages: 0,
        postsPerPage: 8,
        hasNextPage: false,
        hasPreviousPage: false
      };
    }
  }, []);

  // Update URL and apply filters
  const updateURL = useCallback((newFilters: typeof filters, page: number = 1) => {
    const params = createSearchParams(newFilters, page);
    const url = params ? `/blog?${params}` : '/blog';
    
    startTransition(async () => {
      try {
        // Fetch filtered data from API or mock data
        const filteredData = await fetchFilteredData(newFilters, page);
        setBlogData(filteredData);
        setIsSearching(false); // Reset search loading state
        router.push(url);
      } catch (error) {
        console.error('Error updating filters:', error);
        setIsSearching(false); // Reset search loading state on error
        // Fallback to current data
        setBlogData(initialData);
      }
    });
  }, [router, createSearchParams, fetchFilteredData, initialData]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Partial<typeof filters>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    updateURL(updatedFilters, 1);
  }, [filters, updateURL]);

  // Handle real-time search when debounced term changes
  useEffect(() => {
    // Only trigger search if the debounced term is different from current filter
    // and we're not in the middle of a transition
    if (debouncedSearchTerm !== filters.search && !isPending) {
      // Only trigger for user-initiated changes (minimum 2 characters or clearing search)
      if (debouncedSearchTerm.length >= 2 || (debouncedSearchTerm.length === 0 && filters.search)) {
        setIsSearching(true);
        handleFilterChange({ search: debouncedSearchTerm || undefined });
      }
    }
  }, [debouncedSearchTerm, filters.search, handleFilterChange, isPending]);

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setSearchTerm('');
      e.currentTarget.blur();
    }
  }, []);

  // Handle pagination
  const handlePageChange = useCallback((page: number) => {
    updateURL(filters, page);
  }, [filters, updateURL]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
    updateURL({}, 1);
  }, [updateURL]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(filters.category || filters.tag || filters.search || filters.featured);
  }, [filters]);

  // Format date - shorter format for cards
  const formatDate = useCallback((dateString: string) => {
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(new Date(dateString));
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Sidebar Filters */}
      <div className="w-full lg:w-64 shrink-0">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 sticky top-24">
          {/* Mobile filter toggle */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center gap-2 w-full py-2 px-4 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              <Filter className="h-4 w-4" />
              Filtros
              {hasActiveFilters && (
                <span className="bg-brand-primary text-white text-xs px-2 py-1 rounded-full">
                  {Object.values(filters).filter(Boolean).length}
                </span>
              )}
            </button>
          </div>

          <div className={`space-y-6 ${isFilterOpen ? 'block' : 'hidden lg:block'}`}>
            {/* Search */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Buscar
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar artículos... (ESC para limpiar)"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  className="w-full px-4 py-2 pr-16 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 p-1">
                  {(isSearching || isPending) ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-primary"></div>
                  ) : (
                    <Search className="h-4 w-4 text-gray-400" />
                  )}
                </div>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-8 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Categories */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Categorías
              </label>
              <div className="space-y-2">
                <button
                  onClick={() => handleFilterChange({ category: undefined })}
                  className={`block w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    !filters.category
                      ? 'bg-brand-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Todas las categorías
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleFilterChange({ 
                      category: category.slug === filters.category ? undefined : category.slug 
                    })}
                    className={`block w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      filters.category === category.slug
                        ? 'bg-brand-primary text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span>{category.name}</span>
                      <span className="text-xs opacity-75">
                        {category.postCount}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Featured filter */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.featured || false}
                  onChange={(e) => handleFilterChange({ 
                    featured: e.target.checked ? true : undefined 
                  })}
                  className="rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
                />
                <span className="text-sm font-medium text-gray-900">
                  Solo artículos destacados
                </span>
              </label>
            </div>

            {/* Clear filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 w-full py-2 px-4 text-sm text-brand-primary border border-brand-primary rounded-md hover:bg-brand-primary hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1">
        {/* Results info */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-gray-600">
            {blogData.totalPosts > 0 ? (
              <div>
                <p>
                  Mostrando {((blogData.currentPage - 1) * blogData.postsPerPage) + 1}-
                  {Math.min(blogData.currentPage * blogData.postsPerPage, blogData.totalPosts)} de{' '}
                  {blogData.totalPosts} artículos
                </p>
                {filters.search && (
                  <p className="text-sm text-gray-500 mt-1">
                    Resultados para: <span className="font-medium">"{filters.search}"</span>
                  </p>
                )}
              </div>
            ) : (
              <div>
                <p>No se encontraron artículos</p>
                {filters.search && (
                  <p className="text-sm text-gray-500 mt-1">
                    No hay resultados para: <span className="font-medium">"{filters.search}"</span>
                  </p>
                )}
              </div>
            )}
          </div>
          
          {(isPending || isSearching) && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-primary"></div>
              {isSearching ? 'Buscando...' : 'Cargando...'}
            </div>
          )}
        </div>

        {/* Blog Posts Grid */}
        {(isPending || isSearching) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
                <div className="aspect-video bg-gray-200"></div>
                <div className="p-6">
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : blogData.posts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {blogData.posts.map((post) => (
              <BlogPostCard 
                key={post.id} 
                post={post} 
                formatDate={formatDate} 
                searchTerm={filters.search || ''}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 animate-fade-in">
            <div className="text-gray-400 mb-4">
              <Search className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filters.search ? 
                `No se encontraron artículos para "${filters.search}"` : 
                'No se encontraron artículos'
              }
            </h3>
            <p className="text-gray-600 mb-6">
              {filters.search ? 
                'Intenta con términos diferentes o revisa la ortografía.' :
                'Intenta ajustar los filtros o realizar una búsqueda diferente.'
              }
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover transition-colors"
              >
                <X className="h-4 w-4" />
                Limpiar filtros
              </button>
            )}
          </div>
        )}

        {/* Pagination */}
        {blogData.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => handlePageChange(blogData.currentPage - 1)}
              disabled={!blogData.hasPreviousPage || isPending}
              className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </button>
            
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, blogData.totalPages) }, (_, i) => {
              const startPage = Math.max(1, blogData.currentPage - 2);
              const pageNumber = startPage + i;
              
              if (pageNumber > blogData.totalPages) return null;
              
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  disabled={isPending}
                  className={`px-3 py-2 text-sm border rounded-md transition-colors ${
                    pageNumber === blogData.currentPage
                      ? 'bg-brand-primary text-white border-brand-primary'
                      : 'border-gray-300 hover:bg-gray-50'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {pageNumber}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(blogData.currentPage + 1)}
              disabled={!blogData.hasNextPage || isPending}
              className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Siguiente
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Blog Post Image Component with error handling
function BlogPostImage({ post }: { post: BlogPost }) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Check if image URL is valid
  const hasValidImage = post.featuredImage && 
    post.featuredImage.trim() !== '' && 
    !imageError;

  return (
    <div className="relative aspect-video bg-gray-200 overflow-hidden">
      {hasValidImage ? (
        <>
          <Image
            src={post.featuredImage!}
            alt={post.title}
            fill
            className={`object-cover group-hover:scale-105 transition-transform duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse" />
          )}
        </>
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          <FileText className="h-16 w-16 text-gray-400" />
        </div>
      )}
      
      {/* Featured badge */}
      {post.featured && (
        <div className="absolute top-3 left-3">
          <span className="bg-yellow-500 text-white text-xs font-medium px-2 py-1 rounded-full">
            Destacado
          </span>
        </div>
      )}
    </div>
  );
}

// Blog Post Card Component
function BlogPostCard({
  post,
  formatDate,
  searchTerm
}: {
  post: BlogPost;
  formatDate: (date: string) => string;
  searchTerm: string;
}) {
  return (
    <article className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg hover:border-gray-200 transition-all duration-300 hover:-translate-y-1 group flex flex-col h-full">
      <Link href={`/blog/${post.slug}`} className="block flex flex-col flex-1">
        {/* Featured Image */}
        <BlogPostImage post={post} />

        {/* Content */}
        <div className="p-5 flex flex-col flex-1">
          {/* Categories */}
          {post.categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {post.categories.slice(0, 2).map((category) => (
                <span
                  key={category}
                  className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary bg-brand-primary/10 px-2.5 py-1 rounded-full"
                >
                  <Tag className="h-3 w-3" />
                  <HighlightText text={category} searchTerm={searchTerm} />
                </span>
              ))}
              {post.categories.length > 2 && (
                <span className="inline-flex items-center text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                  +{post.categories.length - 2}
                </span>
              )}
            </div>
          )}

          {/* Title */}
          <h2 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 group-hover:text-brand-primary transition-colors leading-snug">
            <HighlightText text={post.title} searchTerm={searchTerm} />
          </h2>

          {/* Excerpt */}
          <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
            <HighlightText text={post.excerpt} searchTerm={searchTerm} />
          </p>

          {/* Meta information - aligned to bottom */}
          <div className="flex flex-col gap-3 mt-auto pt-2">
            {/* Date and Reading Time */}
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 shrink-0" />
                <span className="truncate">
                  {post.publishedAt ? formatDate(post.publishedAt) : 'Sin fecha'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4 shrink-0" />
                <span className="whitespace-nowrap">{post.readingTime.text}</span>
              </div>
            </div>
            
            {/* Author */}
            {post.author.name && (
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                {post.author.avatar && (
                  <Image
                    src={post.author.avatar}
                    alt={post.author.name}
                    width={24}
                    height={24}
                    className="rounded-full shrink-0"
                  />
                )}
                <div className="flex items-center gap-1 min-w-0">
                  <User className="h-3 w-3 text-gray-400 shrink-0" />
                  <span className="text-xs font-medium text-gray-600 truncate">
                    {post.author.name}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}